import type { ShapeManager } from "../../core/ShapeManager";
import { Button } from "../elements/Button";
import { Container } from "../elements/Container";
import type { CommandManager } from "../../core/CommandManager";
import { ImageShape } from "../../core/shapes/ImageShape";
import type { ToolMode, ToolModeManager } from "../../core/ToolModeManager";
import type { ViewportManager } from "../../core/ViewportManager";

export class TopBar extends Container {
    private toolsGroup: Container;
    private historyGroup: Container;
    private shapeManager: ShapeManager;
    private commandManager: CommandManager;
    private toolModeManager: ToolModeManager;
    private viewportManager: ViewportManager;
    private selectBtn!: Button;
    private handBtn!: Button;
    private undoBtn!: Button;
    private redoBtn!: Button;
    private fileInput!: HTMLInputElement;

    constructor(shapeManager: ShapeManager, commandManager: CommandManager, toolModeManager: ToolModeManager, viewportManager: ViewportManager) {
        super('top-bar');
        this.shapeManager = shapeManager;
        this.commandManager = commandManager;
        this.toolModeManager = toolModeManager;
        this.viewportManager = viewportManager;
        this.toolsGroup = new Container('tools-group');
        this.historyGroup = new Container('tools-group');
        this.setupTools();
        this.toolModeManager.on('modeChanged', (mode) => this.updateToolButtons(mode));
        this.commandManager.on('historyChanged', () => this.updateHistoryButtons());
    }

    private setupTools() {
        const title = new Container('canvas-title');
        title.getElement().textContent = 'Untitled canvas';

        this.selectBtn = this.createModeButton('Select', 'select');
        this.handBtn = this.createModeButton('Hand', 'hand');
        const uploadBtn = this.createUploadButton();
        this.undoBtn = this.createUndoButton();
        this.redoBtn = this.createRedoButton();
        const status = new Container('save-status');
        status.getElement().textContent = 'Saved';

        this.toolsGroup.addChildren(this.selectBtn, this.handBtn, uploadBtn);
        this.historyGroup.addChildren(this.undoBtn, this.redoBtn, status);
        this.addChild(title);
        this.addChild(this.toolsGroup);
        this.addChild(this.historyGroup);
        this.updateToolButtons(this.toolModeManager.getMode());
        this.updateHistoryButtons();
    }

    private createModeButton(label: string, mode: ToolMode): Button {
        const button = new Button('tool-btn').setText(label);
        button.onClick(() => this.toolModeManager.setMode(mode));
        return button;
    }

    private createUploadButton(): Button {
        this.fileInput = document.createElement('input');
        this.fileInput.type = 'file';
        this.fileInput.accept = 'image/*';
        this.fileInput.hidden = true;
        this.getElement().appendChild(this.fileInput);

        this.fileInput.addEventListener('change', () => {
            const file = this.fileInput.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (event) => {
                const imageShape = new ImageShape(event.target?.result as string);
                const bounds = this.viewportManager.getVisibleBounds();
                imageShape.position(
                    bounds.left + (bounds.right - bounds.left) / 2,
                    bounds.top + (bounds.bottom - bounds.top) / 2
                );
                this.shapeManager.addShape(imageShape);
                this.fileInput.value = '';
            };
            reader.readAsDataURL(file);
        });

        return new Button('tool-btn').setText('+').onClick(() => this.fileInput.click());
    }

    private createUndoButton(): Button {
        return new Button('tool-btn').setText('Undo').onClick(() => this.commandManager.undo());
    }

    private createRedoButton(): Button {
        return new Button('tool-btn').setText('Redo').onClick(() => this.commandManager.redo());
    }



    private updateToolButtons(mode: ToolMode): void {
        this.selectBtn.getElement().classList.toggle('tool-btn--active', mode === 'select');
        this.handBtn.getElement().classList.toggle('tool-btn--active', mode === 'hand');
    }

    private updateHistoryButtons(): void {
        (this.undoBtn.getElement() as HTMLButtonElement).disabled = !this.commandManager.canUndo();
        (this.redoBtn.getElement() as HTMLButtonElement).disabled = !this.commandManager.canRedo();
    }
}
